import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';

/**
 * Forces stale tabs onto a newly deployed frontend.
 *
 * Every build writes `version.json` next to `index.html` (scripts/write-version-file.js) while the
 * bundle itself carries `environment.appVersion`, baked in from package.json. A tab that has been
 * open across a deploy is still running the old bundle, so the two disagree - that mismatch is the
 * whole signal. When it appears, the tab reloads itself and comes back on the deployed build.
 *
 * `ChunkErrorHandler` covers the same staleness *reactively*, but only once the user happens to
 * navigate to a lazy route whose chunk hash has changed. This check is the proactive half: an idle
 * dashboard picks up the new build without anyone touching it.
 *
 * Deliberate choices:
 * - Native `fetch`, not `HttpClient`: the poll must not pick up `authInterceptor` headers (the file
 *   is a static object on S3, an `Authorization` header would force a CORS preflight) and must not
 *   drive `fuseLoadingInterceptor`, which would flash the global loading bar on every tick.
 * - Timers and listeners run outside Angular so a permanent interval never keeps the zone unstable.
 * - The reload is skipped while the user is mid-edit and retried shortly after, so a deploy cannot
 *   throw away a half-written message or a filled-in dialog. Remove `isUserBusy()` to make the
 *   refresh unconditional.
 * - At most one reload per target version per tab session: if the reload comes back on the old
 *   bundle anyway (a CDN edge still serving stale `index.html`), the tab stops instead of looping.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService implements OnDestroy {
    /** How often an open tab asks the server which version is deployed. */
    private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
    /** Retry cadence while a reload is held back because the user is busy. */
    private static readonly BUSY_RETRY_MS = 15 * 1000;
    /** Floor between event-driven checks (tab focus, visibility) so refocusing cannot spam the CDN. */
    private static readonly MIN_EVENT_GAP_MS = 30 * 1000;
    private static readonly RELOAD_KEY = 'sai-version-reload-target';
    private static readonly VERSION_FILE = 'version.json';

    private readonly _zone = inject(NgZone);
    private readonly _localVersion = String(environment.appVersion ?? '');

    private _pollTimer: ReturnType<typeof setInterval> | null = null;
    private _busyTimer: ReturnType<typeof setTimeout> | null = null;
    private _lastCheckAt = 0;
    private _started = false;

    private readonly _onWake = (): void => {
        void this._checkIfDue();
    };

    /** Starts polling. Idempotent - repeated calls are ignored. */
    start(): void {
        if (this._started || typeof window === 'undefined') {
            return;
        }
        this._started = true;

        this._zone.runOutsideAngular(() => {
            this._pollTimer = setInterval(
                () => void this.checkNow(),
                VersionCheckService.POLL_INTERVAL_MS
            );
            window.addEventListener('focus', this._onWake);
            document.addEventListener('visibilitychange', this._onWake);

            // A tab restored from the bfcache, or one whose index.html came from the browser cache,
            // can be stale the moment it boots - so check immediately rather than in five minutes.
            void this.checkNow();
        });
    }

    stop(): void {
        if (this._pollTimer !== null) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
        this._clearBusyTimer();
        window.removeEventListener('focus', this._onWake);
        document.removeEventListener('visibilitychange', this._onWake);
        this._started = false;
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /**
     * Fetches the deployed version and reloads when it differs from the running bundle.
     * Never throws: a missing file, an offline tab, or the SPA fallback returning index.html
     * all resolve to "unknown", which is treated as "no reason to reload".
     */
    async checkNow(): Promise<void> {
        this._lastCheckAt = Date.now();

        const deployed = await this._fetchDeployedVersion();
        if (!deployed || !this._localVersion || deployed === this._localVersion) {
            return;
        }
        if (this._alreadyReloadedFor(deployed)) {
            return;
        }

        if (this.isUserBusy()) {
            this._scheduleBusyRetry();
            return;
        }

        this._markReloadedFor(deployed);
        this.performReload();
    }

    /**
     * True while a reload would destroy work in progress: a non-empty field has focus, or a dialog
     * is open. Checked again on the retry timer, so the refresh lands as soon as the user is idle.
     */
    protected isUserBusy(): boolean {
        const active = document.activeElement as HTMLElement | null;
        if (active && this._isEditable(active) && this._hasContent(active)) {
            return true;
        }
        return !!document.querySelector('mat-dialog-container, .cdk-dialog-container');
    }

    /** Seam for tests - the only place the tab is actually reloaded. */
    protected performReload(): void {
        window.location.reload();
    }

    private async _fetchDeployedVersion(): Promise<string | null> {
        try {
            // Resolved against <base href="/"> so a deep route such as /company/agents/42 does not
            // request /company/agents/version.json and hit the SPA fallback.
            const url = new URL(
                VersionCheckService.VERSION_FILE,
                document.baseURI
            );
            url.searchParams.set('_', String(Date.now()));

            const response = await fetch(url.toString(), {
                cache: 'no-store',
                credentials: 'omit',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                return null;
            }

            // Rejects when the SPA fallback served index.html instead (e.g. `ng serve`, where no
            // version file is generated), which is exactly the "unknown, do nothing" case.
            const payload = (await response.json()) as { version?: unknown };
            return typeof payload?.version === 'string' && payload.version
                ? payload.version
                : null;
        } catch {
            return null;
        }
    }

    /** Event-driven check, throttled so focus/visibility churn cannot turn into a request storm. */
    private async _checkIfDue(): Promise<void> {
        if (Date.now() - this._lastCheckAt < VersionCheckService.MIN_EVENT_GAP_MS) {
            return;
        }
        await this.checkNow();
    }

    private _scheduleBusyRetry(): void {
        if (this._busyTimer !== null) {
            return;
        }
        this._busyTimer = setTimeout(() => {
            this._busyTimer = null;
            void this.checkNow();
        }, VersionCheckService.BUSY_RETRY_MS);
    }

    private _clearBusyTimer(): void {
        if (this._busyTimer !== null) {
            clearTimeout(this._busyTimer);
            this._busyTimer = null;
        }
    }

    private _alreadyReloadedFor(version: string): boolean {
        try {
            return (
                sessionStorage.getItem(VersionCheckService.RELOAD_KEY) === version
            );
        } catch {
            return false;
        }
    }

    private _markReloadedFor(version: string): void {
        try {
            sessionStorage.setItem(VersionCheckService.RELOAD_KEY, version);
        } catch {
            /* private mode - the reload still happens, it just is not guarded */
        }
    }

    private _isEditable(element: HTMLElement): boolean {
        if (element.isContentEditable) {
            return true;
        }
        const tag = element.tagName;
        if (tag === 'TEXTAREA') {
            return true;
        }
        if (tag !== 'INPUT') {
            return false;
        }
        const type = (element as HTMLInputElement).type;
        return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file'].includes(
            type
        );
    }

    private _hasContent(element: HTMLElement): boolean {
        if (element.isContentEditable) {
            return !!element.textContent?.trim();
        }
        return !!(element as HTMLInputElement | HTMLTextAreaElement).value?.trim();
    }
}
